/* What a Subscription is to the domain — STORY-032.
 *
 * Four statuses, not Stripe's eight. The mapping from the provider's vocabulary
 * to this one lives in the adapter, where the vendor's types are allowed to be
 * — `architecture.md`. What is here is the part that is a business rule: which
 * states are owed the product, and whether a given event is allowed to change
 * anything.
 *
 * Both rules are here rather than in a route or a query because both are wrong
 * invisibly. Nothing throws when a paying customer silently loses their plan.
 */
/**
 * The states a Subscription can be in, as this product understands them.
 *
 * Deliberately coarser than the provider's, but not everywhere. Stripe's
 * `incomplete_expired` folds into `incomplete` because the difference changes
 * nothing anyone can act on.
 *
 * `unpaid` does NOT fold into `past_due`, and it used to. That mapping meant a
 * customer the provider had given up collecting from kept their paid plan
 * indefinitely, because `past_due` grants on purpose. The two look adjacent and
 * are opposites: one is a renewal still being retried, the other is the end of
 * those retries with no money. STORY-045.
 */
export const SUBSCRIPTION_STATUSES = [
    'incomplete',
    'active',
    'past_due',
    /**
     * The provider tried, retried, and stopped trying — STORY-045.
     *
     * Its own state rather than folded into `past_due`, which grants, or into
     * `canceled`, which nobody chose. `past_due` means a renewal is inside the
     * provider's retry schedule and the customer is still owed the product; this
     * means those retries are over and no money arrived. Mapping it onto either
     * neighbour loses the distinction a customer would want stated accurately on
     * their billing screen.
     */
    'unpaid',
    'canceled',
];
/**
 * Does this status entitle the workspace to the Plan it subscribed to?
 *
 * **`past_due` grants, and that is the answer people expect to be wrong.** A
 * past-due card is a renewal that failed and is inside the provider's retry
 * schedule. The customer has not cancelled and has not asked to leave; locking
 * them out the moment a card expires turns a recoverable payment failure into a
 * support ticket and, usually, a refund. When that stops being true is
 * STORY-034's decision, not this one's — and it is a decision about *how long*,
 * which means it needs a date to compare against and therefore does not belong
 * in a pure status check.
 *
 * `incomplete` does not grant: the first payment has not succeeded, and a
 * Checkout Session that was opened and abandoned lands here.
 */
export function grantsEntitlement(status) {
    switch (status) {
        case 'active':
        case 'past_due':
            return true;
        case 'incomplete':
        case 'unpaid':
        case 'canceled':
            return false;
    }
}
/** Whether the subscription is over and will not come back on its own. */
export function isTerminal(status) {
    /* `unpaid` is NOT terminal: the provider has stopped retrying, but paying
     * still revives it. Terminal means it will not come back on its own, and
     * only a cancellation is that. */
    return status === 'canceled';
}
/**
 * May this event overwrite the state we already hold?
 *
 * The story's requirement: *"an older state never overwrites a newer one."*
 * Webhooks arrive out of order as a matter of routine, and without this a
 * delayed `subscription.created` landing after `subscription.updated` resets a
 * live subscription to `incomplete` — a paying customer downgraded by a race.
 *
 * **Equal timestamps apply, and that is not sloppiness.** Stripe's
 * `event.created` has *second* granularity, and one checkout routinely emits
 * several events within the same second — `customer.subscription.created` and
 * `customer.subscription.updated` arrive together constantly. A strict `>`
 * would discard the second of them, leaving the subscription `incomplete`
 * after a successful payment: the customer is charged and gets nothing, and
 * nothing in the logs looks like an error.
 *
 * What prevents the *same* event being applied twice is therefore not this
 * function. It is the unique constraint on the provider's event id, which is
 * the right tool for it: identity is exact where time here is not. The two
 * mechanisms are deliberately separate and neither substitutes for the other.
 */
export function shouldApplyEvent(applied, incoming) {
    if (applied === null)
        return true;
    return incoming.occurredAt.getTime() >= applied.occurredAt.getTime();
}
//# sourceMappingURL=subscription.js.map